import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const btn = "border border-[var(--phosphor)] px-2 py-1 hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed";
const field = "bg-black border border-[color-mix(in_srgb,var(--phosphor)_55%,transparent)] text-[var(--phosphor)] px-2 py-1 outline-none";

const se = (row) => {
  if (row?.season_number == null || row?.episode_number == null) return "S?? E??";
  return `S${String(row.season_number).padStart(2, "0")} E${String(row.episode_number).padStart(2, "0")}`;
};

const durationText = (value) => {
  const seconds = Number(value || 0);
  if (!(seconds > 0)) return "MISSING";
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
};

const sortedMappings = (rows = []) => [...rows].sort((a, b) => {
  const an = Number(a?.channel?.number);
  const bn = Number(b?.channel?.number);
  if (Number.isFinite(an) && Number.isFinite(bn) && an !== bn) return an - bn;
  if (Number.isFinite(an) && !Number.isFinite(bn)) return -1;
  if (!Number.isFinite(an) && Number.isFinite(bn)) return 1;
  return String(a?.show_title || "").localeCompare(String(b?.show_title || ""));
});

export default function S4TvManagementPanel({ apiBase, authHeaders, append, onUploaded }) {
  const [catalogue, setCatalogue] = useState({ mappings: [] });
  const [mappingId, setMappingId] = useState("");
  const [library, setLibrary] = useState(null);
  const [videoId, setVideoId] = useState("");
  const [renameValue, setRenameValue] = useState("");
  const [moveMappingId, setMoveMappingId] = useState("");
  const [replaceFile, setReplaceFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const loadCatalogue = useCallback(async () => {
    try {
      const { data } = await axios.get(`${apiBase}/mega-tv-sync/s4-manage/library`, { headers: authHeaders() });
      const mappings = sortedMappings(data?.mappings || []);
      setCatalogue({ ...(data || {}), mappings });
      setMappingId((current) => current || mappings[0]?.mapping_id || "");
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setError(detail);
      append?.(`> S4 TV MANAGEMENT ERROR: ${detail}`);
    }
  }, [apiBase, authHeaders, append]);

  const loadLibrary = useCallback(async (id) => {
    if (!id) {
      setLibrary(null);
      return;
    }
    try {
      const { data } = await axios.get(`${apiBase}/mega-tv-sync/s4-manage/library/${encodeURIComponent(id)}`, { headers: authHeaders() });
      setLibrary(data || null);
      setVideoId((current) => (data?.episodes || []).some((r) => r.id === current) ? current : (data?.episodes?.[0]?.id || ""));
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setError(detail);
      append?.(`> S4 TV LIBRARY ERROR: ${detail}`);
    }
  }, [apiBase, authHeaders, append]);

  useEffect(() => { loadCatalogue(); }, [loadCatalogue]);
  useEffect(() => { loadLibrary(mappingId); }, [mappingId, loadLibrary]);

  const episode = useMemo(
    () => (library?.episodes || []).find((row) => row.id === videoId) || null,
    [library, videoId]
  );

  useEffect(() => {
    if (!episode) {
      setRenameValue("");
      setReplaceFile(null);
      setProgress(0);
      return;
    }
    setRenameValue((episode.s4_object_key || "").split("/").pop() || "");
    setReplaceFile(null);
    setProgress(0);
    setMoveMappingId("");
    setStatus("");
    setError("");
  }, [episode]);

  const refresh = async (...channelIds) => {
    await loadCatalogue();
    await loadLibrary(mappingId);
    if (onUploaded) {
      for (const id of [...new Set(channelIds.filter(Boolean))]) await onUploaded(id);
    }
  };

  const renameEpisode = async () => {
    if (!episode || !renameValue.trim()) return;
    setBusy(true); setError(""); setStatus("RENAMING");
    append?.(`> S4 TV RENAME — ${se(episode)} ${episode.title}`);
    try {
      const { data } = await axios.post(`${apiBase}/mega-tv-sync/s4-manage/rename`,
        { video_id: episode.id, filename: renameValue.trim() },
        { headers: { "Content-Type": "application/json", ...authHeaders() } });
      setStatus("RENAME COMPLETE");
      append?.(`> S4 TV RENAME COMPLETE — ${data.object_key}`);
      await refresh(episode.channel_id);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setError(detail); setStatus("RENAME FAILED"); append?.(`> S4 TV RENAME ERROR: ${detail}`);
    } finally { setBusy(false); }
  };

  const moveEpisode = async () => {
    if (!episode || !moveMappingId) return;
    const dest = (catalogue.mappings || []).find((row) => row.mapping_id === moveMappingId);
    if (!window.confirm(`Move ${se(episode)} — ${episode.title} to ${dest?.show_title || "the selected show"}?\n\nSeason/episode identity and TMDb metadata will be preserved.`)) return;
    setBusy(true); setError(""); setStatus("MOVING");
    append?.(`> S4 TV MOVE — ${se(episode)} ${episode.title}`);
    try {
      const sourceChannel = episode.channel_id;
      const { data } = await axios.post(`${apiBase}/mega-tv-sync/s4-manage/move`,
        { video_id: episode.id, destination_mapping_id: moveMappingId },
        { headers: { "Content-Type": "application/json", ...authHeaders() } });
      setStatus("MOVE COMPLETE");
      append?.(`> S4 TV MOVE COMPLETE — ${data.to?.show_title || "destination"}`);
      setMappingId(moveMappingId);
      setVideoId(episode.id);
      await loadCatalogue();
      await loadLibrary(moveMappingId);
      if (onUploaded) {
        await onUploaded(sourceChannel);
        if (data.to?.channel_id) await onUploaded(data.to.channel_id);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setError(detail); setStatus("MOVE FAILED"); append?.(`> S4 TV MOVE ERROR: ${detail}`);
    } finally { setBusy(false); }
  };

  const replaceEpisode = async () => {
    if (!episode || !replaceFile) return;
    if (!window.confirm(`Replace the physical S4 file for ${se(episode)} — ${episode.title} with "${replaceFile.name}"?\n\nThe replacement is probed before promotion. Tape ID, S/E identity, TMDb metadata and playlist position are preserved.`)) return;
    setBusy(true); setError(""); setProgress(0); setStatus("PREPARING REPLACEMENT");
    append?.(`> S4 TV REPLACE PRECHECK — ${se(episode)} ${episode.title}`);
    try {
      const { data: prep } = await axios.post(`${apiBase}/mega-tv-sync/s4-manage/replace/prepare`, {
        video_id: episode.id,
        filename: replaceFile.name,
        size: replaceFile.size,
        content_type: replaceFile.type || "application/octet-stream",
      }, { headers: { "Content-Type": "application/json", ...authHeaders() } });

      setStatus("UPLOADING REPLACEMENT TO S4");
      await axios.put(prep.upload_url, replaceFile, {
        headers: replaceFile.type ? { "Content-Type": replaceFile.type } : {},
        onUploadProgress: (evt) => {
          const total = evt.total || replaceFile.size || 1;
          setProgress(Math.max(0, Math.min(100, Math.round((evt.loaded / total) * 100))));
        },
      });

      setProgress(100); setStatus("PROBING + PROMOTING REPLACEMENT");
      const { data } = await axios.post(`${apiBase}/mega-tv-sync/s4-manage/replace/finalize`, {
        video_id: episode.id,
        temp_object_key: prep.temp_object_key,
        expected_size: prep.expected_size,
      }, { headers: { "Content-Type": "application/json", ...authHeaders() } });

      setStatus(data.guide_ready ? "REPLACEMENT COMPLETE — GUIDE READY" : "REPLACEMENT COMPLETE — ATTENTION REQUIRED");
      append?.(`> S4 TV REPLACE COMPLETE — DURATION ${durationText(data.duration_sec)} · GUIDE ${data.guide_ready ? "READY" : "BLOCKED"}`);
      setReplaceFile(null); setProgress(0);
      await refresh(episode.channel_id);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setError(detail); setStatus("REPLACE FAILED"); setProgress(0); append?.(`> S4 TV REPLACE ERROR: ${detail}`);
    } finally { setBusy(false); }
  };

  const deleteEpisode = async () => {
    if (!episode) return;
    if (!window.confirm(`PERMANENTLY remove ${se(episode)} — ${episode.title}?\n\nThis deletes the physical S4 object and soft-deletes the catalogue tape.`)) return;
    setBusy(true); setError(""); setStatus("DELETING");
    append?.(`> S4 TV DELETE — ${se(episode)} ${episode.title}`);
    try {
      const channelId = episode.channel_id;
      const { data } = await axios.post(`${apiBase}/mega-tv-sync/s4-manage/delete`,
        { video_id: episode.id },
        { headers: { "Content-Type": "application/json", ...authHeaders() } });
      setStatus("DELETE COMPLETE"); append?.(`> S4 TV DELETE COMPLETE — ${data.title}`);
      setVideoId(""); await refresh(channelId);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setError(detail); setStatus("DELETE FAILED"); append?.(`> S4 TV DELETE ERROR: ${detail}`);
    } finally { setBusy(false); }
  };

  const readiness = library?.readiness || {};
  const missing = Number(readiness.missing_durations || 0);

  return (
    <section className="border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] p-3 scroll-mt-3" data-testid="s4-tv-management" id="s4-tv-management">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.16em] font-bold">S4 TV MANAGEMENT</div>
          <div className="mt-1 text-[9px] opacity-65">
            MOVE, RENAME, REPLACE OR REMOVE A PRODUCTION S4 TV EPISODE. PHYSICAL STORAGE AND CATALOGUE STAY IN SYNC.
          </div>
        </div>
        <button type="button" className={btn} disabled={busy} onClick={async () => { await loadCatalogue(); await loadLibrary(mappingId); }}>REFRESH</button>
      </div>

      <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-2">
        <label className="text-[9px]">
          <span className="block mb-1 opacity-65">SHOW / CHANNEL</span>
          <select value={mappingId} disabled={busy} onChange={(e) => { setMappingId(e.target.value); setVideoId(""); }} className={`${field} w-full`}>
            {!(catalogue.mappings || []).length && <option value="">NO S4 TV SHOWS</option>}
            {(catalogue.mappings || []).map((row) => (
              <option key={row.mapping_id} value={row.mapping_id}>
                {row.channel ? `CH ${row.channel.number} — ${row.show_title}` : row.show_title} · {row.episode_count} TAPES
              </option>
            ))}
          </select>
        </label>
        <label className="text-[9px]">
          <span className="block mb-1 opacity-65">EPISODE</span>
          <select value={videoId} disabled={busy || !mappingId} onChange={(e) => setVideoId(e.target.value)} className={`${field} w-full`}>
            {!(library?.episodes || []).length && <option value="">NO S4 EPISODES</option>}
            {(library?.episodes || []).map((row) => (
              <option key={row.id} value={row.id}>{se(row)} — {row.title}</option>
            ))}
          </select>
        </label>
      </div>

      {episode && (
        <div className="mt-3 border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] p-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px]">{se(episode)} — {episode.title}</div>
              <div className="mt-1 text-[8px] opacity-55 break-all">s4://{episode.s4_bucket}/{episode.s4_object_key}</div>
              <div className="mt-1 text-[8px] opacity-55">TMDB {episode.tmdb_id || "—"} · ORDER {episode.order ?? "—"} · DURATION {durationText(episode.duration_sec)}</div>
              <div className="mt-1 text-[8px] opacity-55">SHOW {readiness.episodes ?? 0} EPISODES · DURATIONS {readiness.valid_durations ?? 0}/{readiness.episodes ?? 0} · MISSING {missing}</div>
            </div>
            <div className={`text-[9px] tracking-widest ${Number(episode.duration_sec || 0) > 0 ? "" : "text-red-300"}`}>
              {Number(episode.duration_sec || 0) > 0 ? "GUIDE READY" : "GUIDE BLOCKED — DURATION MISSING"}
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 xl:grid-cols-[1fr_auto] gap-2 items-end">
            <label className="text-[9px]"><span className="block mb-1 opacity-65">RENAME S4 FILE</span><input value={renameValue} disabled={busy} onChange={(e) => setRenameValue(e.target.value)} className={`${field} w-full`} /></label>
            <button type="button" className={btn} disabled={busy || !renameValue.trim()} onClick={renameEpisode}>RENAME</button>
          </div>

          <div className="mt-3 grid grid-cols-1 xl:grid-cols-[1fr_auto] gap-2 items-end">
            <label className="text-[9px]"><span className="block mb-1 opacity-65">MOVE TO TV SHOW</span><select value={moveMappingId} disabled={busy} onChange={(e) => setMoveMappingId(e.target.value)} className={`${field} w-full`}><option value="">SELECT DESTINATION SHOW</option>{(catalogue.mappings || []).filter((row) => row.mapping_id !== mappingId).map((row) => <option key={row.mapping_id} value={row.mapping_id}>{row.channel ? `CH ${row.channel.number} — ` : ""}{row.show_title}</option>)}</select></label>
            <button type="button" className={btn} disabled={busy || !moveMappingId} onClick={moveEpisode}>MOVE</button>
          </div>

          <div className="mt-3 grid grid-cols-1 xl:grid-cols-[1fr_auto] gap-2 items-end">
            <label className="text-[9px]"><span className="block mb-1 opacity-65">REPLACE VIDEO FILE</span><input type="file" accept=".mp4,.mkv,.avi,.mov,.webm,.m4v,.wmv,.flv,.mpg,.mpeg,.ts,.m2ts,.3gp,.ogv,video/*" disabled={busy} onChange={(e) => { setReplaceFile(e.target.files?.[0] || null); setProgress(0); setStatus(""); setError(""); }} className={`${field} w-full text-[9px]`} /></label>
            <button type="button" className={btn} disabled={busy || !replaceFile} onClick={replaceEpisode}>REPLACE</button>
          </div>

          {progress > 0 && <div className="mt-2"><div className="flex justify-between text-[8px] opacity-65"><span>{status || "REPLACING"}</span><span>{progress}%</span></div><div className="mt-1 h-2 border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)]"><div className="h-full bg-[var(--phosphor)] transition-[width]" style={{ width: `${progress}%` }} /></div></div>}

          <div className="mt-3 pt-3 border-t border-[color-mix(in_srgb,var(--phosphor)_20%,transparent)] flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[8px] opacity-55">REPLACE PROBES THE TEMP FILE BEFORE PROMOTION. REMOVE DELETES S4 THEN SOFT-DELETES THE TAPE.</div>
            <button type="button" disabled={busy} onClick={deleteEpisode} className="border border-red-500/70 text-red-400 px-2 py-1 hover:bg-red-500 hover:text-black transition-colors disabled:opacity-40">REMOVE EPISODE</button>
          </div>
        </div>
      )}

      {status && progress === 0 && <div className="mt-2 text-[8px] opacity-65">{status}</div>}
      {error && <div className="mt-2 border border-red-500/45 px-2 py-1 text-[9px] text-red-300">{error}</div>}
    </section>
  );
}
